import React, { FormEvent, useState } from "react";
import { useFragment, useMutation } from "react-relay";
import graphql from "babel-plugin-relay/macro";
import styled from "styled-components/macro";

import { SongAddShare_song$key } from "../__generated__/SongAddShare_song.graphql";
import { SongAddShareMutation } from "../__generated__/SongAddShareMutation.graphql";

import Button from "../_components/Button";
import { TextInputIcon } from "../_components/TextInputIcon";
import { Select } from "../_components/Select";
import { Icon } from "../_components/Icon";
import { AddShareInputsWrapper } from "./SongManageShare";
import { CustomErrors } from "../_types/errorTypes";
import { ModalError } from "../_components/ModalError";
import { FriendlyErrors } from "../_components/FriendlyErrors";
import {
  InputSuggest,
  InputValueSuggestion,
} from "../_components/InputSuggest";
import { addBreaksToEmail } from "../_utilities/strings";
import { ShareRelation } from "../UserSongs/UserSongsList";
import { Modal } from "../_components/Modal";
import {
  ModalTitle,
  ModalHeader,
  ModalContent,
} from "../_style-components/Modal.style";
import { TitleArtist } from "../_style-components/TitleArtist";
import { FORM_PLACEHOLDERS } from "../_utilities/forms";

type Props = {
  songRef: SongAddShare_song$key;
  shareRelations: ShareRelation[];
};

export function SongAddShare(props: Props) {
  const song = useFragment(
    graphql`
      fragment SongAddShare_song on Song {
        id
        title
        artist
        mongoVersion
        permissions {
          user
          email
          isOwner
          canEdit
          didAccept
        }
      }
    `,
    props.songRef
  );

  const [errMsg, setErrMsg] = React.useState<string>("");
  const [friendlyErrors, setFriendlyErrors] = React.useState<CustomErrors>([]);

  const [shareEmail, setShareEmail] = useState<string>("");
  const [canEdit, setCanEdit] = useState<boolean>(false);

  const [doShowModal, setDoShowModal] = React.useState<boolean>(false);
  function openModal() {
    setDoShowModal(true);
  }
  function closeModal() {
    setDoShowModal(false);
  }

  const [commit, isInFlight] = useMutation<SongAddShareMutation>(graphql`
    mutation SongAddShareMutation($input: addSongShareInput!) {
      addSongShare(input: $input) {
        song {
          id
          mongoVersion
          permissions {
            user
            email
            isOwner
            canEdit
            didAccept
          }
        }
        errors {
          __typename
          message
        }
      }
    }
  `);

  function resetForm() {
    setShareEmail("");
  }

  function handleCommit() {
    commit({
      variables: {
        input: {
          songId: song.id,
          shareEmail,
          canEdit,
        },
      },
      onCompleted: (response) => {
        if (response.addSongShare) {
          if (response.addSongShare.song) {
            closeModal();
          }
          const { errors } = response.addSongShare;
          if (errors.length > 0) {
            const errMsgs = errors.map((err) => err.message).join("; ");
            setErrMsg(errMsgs);
            // setFriendlyErrors(errors);
          } else {
            resetForm();
          }
        }
      },
      onError: (err) => {
        setErrMsg(err.message);
      },
    });
  }

  function handleSubmit(e: FormEvent) {
    // make sure e.preventDefault() comes first for form submission
    e.preventDefault();
    handleCommit();
  }

  function handleConfirmModal() {
    handleCommit();
  }

  const valueSuggestions: InputValueSuggestion[] = props.shareRelations
    .filter(
      (val) =>
        !song.permissions.find((permission) => permission.email === val.email)
    )
    // sort by shareCount
    .sort((a, b) => b.shareCount - a.shareCount)
    .map((val) => {
      const { email } = val;
      const display = addBreaksToEmail(email);
      return {
        key: email,
        value: email,
        display,
      };
    });

  return (
    <>
      <Button
        onClick={openModal}
        // expand="inline"
        fill="clear"
        disabled={isInFlight}
        iconId="user-plus"
      >
        {/* <Icon id="user-plus" size={28} /> */}
        Share with User
      </Button>

      {/*   <Wrapper onSubmit={handleSubmit}>
        <AddShareInputsWrapper>
          <Button
            type="submit"
            expand="inline"
            fill="clear"
            // size="sm"
            disabled={isInFlight}
          >
            <Icon id="user-plus" size={28} />
          </Button>
          <TextInputWrapper>
            <InputSuggest
              valueSuggestions={valueSuggestions}
              value={shareEmail}
              onChange={setShareEmail}
            />
          </TextInputWrapper>
          <Select
            name="canEdit"
            // label="Can Edit?"
            onChange={(e) =>
              setCanEdit(parseInt(e.target.value) === 1 ? true : false)
            }
            value={canEdit ? 1 : 0}
          >
            <option value={0}>View</option>
            <option value={1}>Edit</option>
          </Select>
        </AddShareInputsWrapper>
      </Wrapper> */}

      <Modal
        doShowDialog={doShowModal}
        closeDialog={closeModal}
        label={"Share"}
        handleConfirm={handleConfirmModal}
        actionText={"Share"}
        showCloseButton={true}
        isInFlight={isInFlight}
        // headerIcon={{ id: "user-plus", color: "var(--color-primary)" }}
      >
        <ModalHeader>
          <TitleArtist title={song.title} artist={song.artist} />
        </ModalHeader>
        <ModalTitle>Share with User</ModalTitle>

        <Form onSubmit={handleSubmit}>
          <AddShareInputsWrapper>
            <TextInputWrapper>
              <InputSuggest
                valueSuggestions={valueSuggestions}
                value={shareEmail}
                onChange={setShareEmail}
                label={"Email address"}
                placeholder={FORM_PLACEHOLDERS.emailOfAnother}
              />
            </TextInputWrapper>
            <Select
              name="canEdit"
              onChange={(e) =>
                setCanEdit(parseInt(e.target.value) === 1 ? true : false)
              }
              value={canEdit ? 1 : 0}
            >
              <option value={0}>View</option>
              <option value={1}>Edit</option>
            </Select>
          </AddShareInputsWrapper>
        </Form>
      </Modal>

      <ModalError errMsg={errMsg} setErrMsg={setErrMsg} />
      {friendlyErrors.length > 0 && <FriendlyErrors errors={friendlyErrors} />}
    </>
  );
}

const Form = styled.form`
  padding-bottom: 16px;
  padding-left: 10px;
`;
const TextInputWrapper = styled.div`
  flex: 1;

  /* constrain long text; NOTE: this took me a while to realize: if you want these to constrain flex-calculated minimum size, these need to be applied to the same element that is "flexing"; e.g. I tried applying these to a child of this element, which resulted in long titles pushing the right-side button off the screen, because they did not reduce the size calculation of this element; */
  overflow: hidden;
`;
